import CopyIcon from '@ingka/ssr-icon/paths/copy';
import { Entity } from '@inter-ikea-core/core';
import { CoreApi } from '@inter-ikea-storageone/range';
import * as LiveAnnouncer from '@react-aria/live-announcer';
import { reportCopyArticle } from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { useHotkeys } from 'react-hotkeys-hook';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import ContextButton from '../ContextButton';

const Wrapper = styled.div``;

const DisabledText = styled.div`
  display: none;
  :not(:focus):not(:active) {
    clip: rect(0 0 0 0);
    clip-path: inset(100%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

type Props = {
  selectedEntity: Entity;
  size?: 'xsmall' | 'small' | 'medium';
};

export const duplicateAction = (
  selectedEntity: Entity,
  coreApi: CoreApi,
  formatMessage: ({ id }: { id: string }) => string
) => {
  const articleNumber = coreApi.entities.getGlobalArticleNumber(selectedEntity);

  if (articleNumber) {
    reportCopyArticle({ articleNumbers: [articleNumber] });
  }

  const announce = (text: string): void => {
    LiveAnnouncer.announce(text, 'polite');
  };

  const duplicateResult = coreApi.history.historyAction(() => {
    const duplicateResult = coreApi.entities.duplicate(selectedEntity);

    if (duplicateResult && duplicateResult.valid && duplicateResult.placedEntity) {
      announce(+' ' + formatMessage({ id: 'storageone.ariaAddToScene' }));
      coreApi.emit.onDuplicate(duplicateResult.placedEntity);
    }
    return duplicateResult;
  });

  if (duplicateResult?.placedEntity) {
    coreApi.entities.select(duplicateResult.placedEntity);
  }
};

const Duplicate = ({ selectedEntity, size }: Props) => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();

  const onDuplicateClicked = () => {
    duplicateAction(selectedEntity, coreApi, formatMessage);
  };

  const onHotkey = (event: KeyboardEvent) => {
    event.preventDefault();
    onDuplicateClicked();
  };

  useHotkeys('ctrl+d', onHotkey, [selectedEntity]);
  useHotkeys('cmd+d', onHotkey, [selectedEntity]);

  return (
    <Wrapper>
      <ContextButton
        label={formatMessage({ id: 'storageone.contextMenuDuplicate' })}
        dataTestId="duplicate"
        onClick={onDuplicateClicked}
        ssrIcon={CopyIcon}
        size={size}
      >
        <DisabledText>{formatMessage({ id: 'storageone.contextMenuDuplicate' })}</DisabledText>
      </ContextButton>
    </Wrapper>
  );
};

export default Duplicate;
